import { useCallback, useEffect, useRef } from 'react';
import { di } from 'react-magnetic-di';

import {
	usePortalId,
	useRequestTypeId,
	useSetProformaFormDirty,
	useProformaFormIsDirty,
} from '../../../../controllers/request-create-context-provider';

/**
 * Hook that returns an onProformaFormDirty callback for use in FIELD_CHANGED lifecycle handler.
 * Dispatches to help-center's Redux store to mark the Proforma form as dirty when any field changes.
 * Uses a ref to avoid redundant dispatches when already dirty (matches legacy optimization).
 * On unmount, dispatches isDirty: false to reset state for submit/cancel/revisit flows.
 */
export const useProformaFormDirty = () => {
	di(useSetProformaFormDirty, usePortalId, useRequestTypeId, useProformaFormIsDirty);

	const setProformaFormDirtyAction = useSetProformaFormDirty();
	const portalId = usePortalId();
	const requestTypeId = useRequestTypeId();
	const proformaFormIsDirty = useProformaFormIsDirty();

	const proformaFormIsDirtyRef = useRef(proformaFormIsDirty);

	useEffect(() => {
		proformaFormIsDirtyRef.current = proformaFormIsDirty;
	}, [proformaFormIsDirty]);

	const onProformaFormDirty = useCallback(() => {
		if (!proformaFormIsDirtyRef.current && setProformaFormDirtyAction) {
			setProformaFormDirtyAction({ portalId, requestTypeId, isDirty: true });
		}
	}, [portalId, requestTypeId, setProformaFormDirtyAction]);

	useEffect(() => {
		return () => {
			if (setProformaFormDirtyAction) {
				setProformaFormDirtyAction({ portalId, requestTypeId, isDirty: false });
			}
		};
	}, [portalId, requestTypeId, setProformaFormDirtyAction]);

	return onProformaFormDirty;
};
